/*
 * Copyright (C) 2019 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { Action, select, Store } from '@ngrx/store';
import * as tenantActions from './tenant-management-api.actions';
import * as fromTenantManagement from 'app/page-modules/tenant-management/store/tenant-management.selectors';
import { State } from 'app/main/store';
import { filterUndefined, observableOf } from 'app/shared/rxjs/rxjs.utils';
import { TenantDataService } from 'app/page-modules/tenant-management/services/data.service';
import { TenantAdded } from 'app/shared-data/store';

@Injectable()
export class TenantManagementApiEffects {
  /** Create Tenant. */

  createTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.CreateTenantDetails>(tenantActions.CREATE_TENANT_DETAILS),
      switchMap(action =>
        this.dataService.createTenant(action.request).pipe(
          map(value => new tenantActions.CreateTenantDetailsSuccess(value)),
          catchError(err => observableOf(new tenantActions.CreateTenantDetailsError(err)))
        )
      )
    )
  );

  /** Update tenant list on tenant creation. */

  addTenantToTenantList: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.CreateTenantDetailsSuccess>(tenantActions.CREATE_TENANT_DETAILS_SUCCESS),
      map(action => new TenantAdded(action.tenantDetails))
    )
  );

  /** Delete Tenant. */

  deleteTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(tenantActions.DELETE_SELECTED_TENANT_DETAILS),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([, { organizationId }]) =>
        this.dataService.deleteTenant(organizationId).pipe(
          map(() => new tenantActions.DeleteSelectedTenantDetailsSuccess()),
          catchError(err => observableOf(new tenantActions.DeleteSelectedTenantDetailsError(err)))
        )
      )
    )
  );

  /** Load tenant details. */

  loadTenantDetails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.LoadTenantDetails>(tenantActions.LOAD_TENANT_DETAILS),
      switchMap(action =>
        this.dataService.tenantDetails(action.tenantId).pipe(
          map(value => new tenantActions.LoadTenantDetailsSuccess(value)),
          catchError(err => observableOf(new tenantActions.LoadTenantDetailsError(err)))
        )
      )
    )
  );

  /** Load users for tenant. */

  loadUsersForTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(tenantActions.LOAD_USERS_FOR_TENANT),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([, selectedTenant]) =>
        this.dataService.usersForTenant(selectedTenant.organizationId).pipe(
          map(value => new tenantActions.LoadUsersForTenantSuccess(value)),
          catchError(err => observableOf(new tenantActions.LoadUsersForTenantError(err)))
        )
      )
    )
  );

  /** Add read-only role for tenant. */

  addReadOnlyRoleForTenant: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType(tenantActions.ADD_READ_ONLY_ROLE),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([, selectedTenant]) =>
        this.dataService.addReadOnlyRole(selectedTenant.organizationId).pipe(
          map(() => new tenantActions.AddReadOnlyRoleSuccess()),
          catchError(err => observableOf(new tenantActions.AddReadOnlyRoleError(err)))
        )
      )
    )
  );

  /** Update tenant details. */

  updateTenantDetails: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.UpdateTenantDetails>(tenantActions.UPDATE_TENANT_DETAILS),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([{ successMessage, updateRequest }, { organizationId }]) =>
        this.dataService.updateTenantDetails(organizationId, successMessage, updateRequest).pipe(
          map(value => new tenantActions.UpdateTenantDetailsSuccess(successMessage, value)),
          catchError(err => observableOf(new tenantActions.UpdateTenantDetailsError(err)))
        )
      )
    )
  );

  /** Add user for tenant. */

  userAdd: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.AddUserForTenant>(tenantActions.ADD_USER_FOR_TENANT),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([action, { organizationId }]) =>
        this.dataService.addUserForTenant(organizationId, action.user).pipe(
          map(value => new tenantActions.AddUserForTenantSuccess(value)),
          catchError(err => observableOf(new tenantActions.AddUserForTenantError(err)))
        )
      )
    )
  );

  /** Update user details. */

  updateUser: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.UpdateUserForTenant>(tenantActions.UPDATE_USER_FOR_TENANT),
      withLatestFrom(
        this.store.pipe(select(fromTenantManagement.getSelectedTenantData), filterUndefined())
      ),
      switchMap(([action, selectedTenant]) =>
        this.dataService.updateUserForTenant(selectedTenant.organizationId, action.user).pipe(
          map(value => new tenantActions.UpdateUserForTenantSuccess(value)),
          catchError(err => observableOf(new tenantActions.UpdateUserForTenantError(err)))
        )
      )
    )
  );

  /** Delete user for tenant. */

  deleteUser: Observable<Action> = createEffect(() =>
    this.actions.pipe(
      ofType<tenantActions.DeleteUserForTenant>(tenantActions.DELETE_USER_FOR_TENANT),
      switchMap(action =>
        this.dataService.deleteUser(action.user, action.request).pipe(
          map(user => new tenantActions.DeleteUserForTenantSuccess(user)),
          catchError(err => observableOf(new tenantActions.DeleteUserForTenantError(err)))
        )
      )
    )
  );

  constructor(
    private actions: Actions,
    private store: Store<State>,
    private dataService: TenantDataService
  ) {}
}
